import {useLocation, useNavigate} from "react-router-dom";
import { useFranchiseStore } from "../../../store/useFranchise";
import {useEffect, useState} from "react";
import Input from "../../form/Input";
import Button from "../../button/Button";
import { toast } from "react-toastify";
import './franchise.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft} from "@fortawesome/free-solid-svg-icons";

/**
 * @author jinhee
 * @date 2025.08.13
 * @description 가맹점 상세 페이지
 */
const FranchiseUpdate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getFranchiseDetail, franchiseDetail, updateFranchise, updateFranchiseLoading } = useFranchiseStore();
  const [franchise, setFranchise] = useState<any>(null);
  
  // URL에서 직접 ID 추출
  const id = location.pathname.split('/').pop();

  useEffect(() => {
    if (id) {
      getFranchiseDetail(id);
    }
  }, [id, getFranchiseDetail]);
  
  useEffect(() => {
    if (franchiseDetail) {
      setFranchise(franchiseDetail);
    }
  }, [franchiseDetail]);

  // 수정하기
  // 전화번호 포매팅 함수
  const formatPhoneNumber = (value: string) => {
    let phoneInput = value.replace(/\D/g, '');

    if (phoneInput.length > 3 && phoneInput.length <= 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(3)}`;
    } else if (phoneInput.length > 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(3, 7)}-${phoneInput.slice(7, 11)}`;
    }

    return phoneInput;
  };

  // 사업자번호 포매팅 함수
  const formatBusinessNumber = (value: string) => {
    let businessInput = value.replace(/\D/g, '');

    if (businessInput.length > 3 && businessInput.length <= 5) {
      businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(3)}`;
    } else if (businessInput.length > 5) {
      businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(3, 5)}-${businessInput.slice(5, 10)}`;
    }

    return businessInput;
  };

  const handleUpdate = async () => {
    if (!id) {
      toast.error('가맹점 ID가 없습니다.');
      return;
    }

    if (!franchise) {
      toast.error('수정할 데이터가 없습니다.');
      return;
    }

    // 필수 항목 검증
    if (!franchise.ceo) {
      toast.error('대표자명을 입력해주세요.');
      return;
    }
    if (!franchise.companyNumber) {
      toast.error('사업자 번호를 입력해주세요.');
      return;
    }
    if (!franchise.companyName) {
      toast.error('사업자명을 입력해주세요.');
      return;
    }
    if (!franchise.telNo) {
      toast.error('전화번호를 입력해주세요.');
      return;
    }

    // 수정 API 호출
    const updateData = {
      ceo: franchise.ceo,
      companyNumber: franchise.companyNumber,
      companyName: franchise.companyName,
      address: franchise.address,
      telNo: franchise.telNo,
      email: franchise.email,
      bizProfileName: franchise.bizProfileName,
      newPassword: franchise.newPassword,
    };

    updateFranchise(id, updateData, () => {
      navigate('/franchise');
    });
  };

  // 목록으로 이동
  const handleCancel = () => {
    navigate('/franchise');
  };

  return (
    <div className="saju_container">
      <div className="saju_container_box">
        <div className="saju_container_content mt-6">
          {/* 페이지 제목 */}
          <div className="!flex !items-center !justify-between">
            <div className="!flex !items-center !gap-3">
              <button
                  onClick={handleCancel}
                  className="!p-2 !rounded-lg hover:!bg-gray-100 !transition-colors"
              >
                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className="!w-4 !h-4 !text-gray-500"
                />
              </button>
              <h1 className="!text-xl !font-semibold !text-gray-900">
                가맹점 상세 정보
              </h1>
            </div>
          </div>

          {/* 컨텐츠 섹션 */}
          <div className="saju_content_section">
            {/* 기본 정보 */}
            <div className="saju_form_group">
              <h4 className="saju_form_title">기본 정보</h4>
              
              <div className="saju_form_row">
                <div className="saju_form_item">
                  <label className="saju_label required">대표자명</label>
                  <Input
                    value={franchise?.ceo || ''}
                    onChange={(e) => setFranchise({...franchise, ceo: e.target.value})}
                    placeholder="대표자명을 입력하세요"
                    width="100%"
                    textAlign="left"
                  />
                </div>
              </div>

              <div className="saju_form_row">
                <div className="saju_form_item">
                  <label className="saju_label required">전화번호</label>
                  <Input
                    value={franchise?.telNo || ''}
                    onChange={(e) => setFranchise({...franchise, telNo: formatPhoneNumber(e.target.value)})}
                    placeholder="전화번호를 입력하세요"
                    width="100%"
                    textAlign="left"
                  />
                </div>
              </div>

              <div className="saju_form_row">
                <div className="saju_form_item">
                  <label className="saju_label required">비밀번호</label>
                  <Input
                      type="password"
                      value={franchise?.newPassword || ''}
                      onChange={(e) => setFranchise({...franchise, newPassword: e.target.value})}
                      placeholder="수정할 비밀번호를 입력하세요"
                      width="100%"
                      textAlign="left"
                      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
                  />
                </div>
              </div>
            </div>

            {/* 가맹점 정보 */}
            <div className="saju_form_group">
              <h4 className="saju_form_title">가맹점 정보</h4>
              <div className="saju_form_row">
                <div className="saju_form_item">
                  <label className="saju_label required">사업자 번호</label>
                  <Input
                      value={franchise?.companyNumber || ''}
                      onChange={(e) => setFranchise({...franchise, companyNumber: formatBusinessNumber(e.target.value)})}
                      placeholder="사업자 번호를 입력하세요 (예: 123-45-67890)"
                      width="100%"
                      textAlign="left"
                  />
                </div>
                <div className="saju_form_item">
                  <label className="saju_label required">사업자명</label>
                  <Input
                      value={franchise?.companyName || ''}
                      onChange={(e) => setFranchise({...franchise, companyName: e.target.value})}
                      placeholder="사업자명을 입력하세요"
                      width="100%"
                      textAlign="left"
                  />
                </div>
              </div>
              <div className="saju_form_row">
                <div className="saju_form_item full">
                  <label className="saju_label">주소</label>
                  <Input
                      value={franchise?.address || ''}
                      onChange={(e) => setFranchise({...franchise, address: e.target.value})}
                      placeholder="주소를 입력하세요"
                      width="100%"
                      textAlign="left"
                  />
                </div>
              </div>

              <div className="saju_form_row">
                <div className="saju_form_item">
                  <label className="saju_label">이메일</label>
                  <Input
                    value={franchise?.email || ''}
                    onChange={(e) => setFranchise({...franchise, email: e.target.value})}
                    placeholder="이메일을 입력하세요"
                    width="100%"
                    textAlign="left"
                    type="email"
                  />
                </div>
                
                <div className="saju_form_item">
                  <label className="saju_label">비즈프로필</label>
                  <Input
                    value={franchise?.bizProfileName || ''}
                    onChange={(e) => setFranchise({...franchise, bizProfileName: e.target.value})}
                    placeholder="비즈프로필을 입력하세요"
                    width="100%"
                    textAlign="left"
                  />
                </div>
              </div>
            </div>

            {/* 시스템 정보 (읽기 전용) */}
            <div className="saju_form_group">
              <h4 className="saju_form_title">시스템 정보</h4>
              
              <div className="saju_form_row">
                <div className="saju_form_item">
                  <label className="saju_label">가맹점 ID</label>
                  <Input
                    value={id || ''}
                    width="100%"
                    textAlign="left"
                    disabled={true}
                  />
                </div>
                
                <div className="saju_form_item">
                  <label className="saju_label">복비 잔액</label>
                  <Input
                    value={franchise?.chargeAmount ? `${franchise.chargeAmount.toLocaleString()}원` : '0원'}
                    width="100%"
                    textAlign="left"
                    disabled={true}
                  />
                </div>
              </div>

              <div className="saju_form_row">
                <div className="saju_form_item full">
                  <label className="saju_label flex items-center gap-2">카카오 채널 프로필키
                    <span className="relative inline-block group">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-700 text-xs cursor-help">?</span>
                      <span className="absolute left-1/2 -translate-x-1/2 mt-2 hidden group-hover:block z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                        알림톡 전송시 사용되는 키 입니다. 외부 노출되면 안됩니다.
                      </span>
                    </span>
                  </label>
                  <Input
                    value={franchise?.bizmProfileKey || ''}
                    width="100%"
                    textAlign="left"
                    disabled={true}
                  />
                </div>
              </div>

              <div className="saju_form_row">
                <div className="saju_form_item">
                  <label className="saju_label">등록일</label>
                  <Input
                    value={franchise?.createdAt ? new Date(franchise.createdAt).toLocaleDateString() : ''}
                    width="100%"
                    textAlign="left"
                    disabled={true}
                  />
                </div>
                
                <div className="saju_form_item">
                  <label className="saju_label">수정일</label>
                  <Input
                    value={franchise?.updatedAt ? new Date(franchise.updatedAt).toLocaleDateString() : ''}
                    width="100%"
                    textAlign="left"
                    disabled={true}
                  />
                </div>
              </div>
            </div>

            {/* 버튼 영역 */}
            <div className="saju_button_group ">
              <Button
                type="submit"
                text="수정하기"
                onClick={handleUpdate}
                disabled={updateFranchiseLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchiseUpdate;
